import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatMoney } from "../lib/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Package, Clock, MapPin, Plane, Ship, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  payment_hold: { color: "#FFD100", label: "48h Payment Hold" },
  confirmed: { color: "#009A44", label: "Confirmed" },
  shipped: { color: "#2563EB", label: "Shipped" },
  delivered: { color: "#52525B", label: "Delivered" },
  cancelled: { color: "#EF3340", label: "Cancelled" },
};

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const highlight = params.get("order");
  const nav = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login?next=/dashboard"); return; }
    api.get("/orders").then(r => setOrders(r.data)).finally(() => setLoading(false));
  }, [user, authLoading, nav]);

  if (authLoading || loading) {
    return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#009A44]" size={28} /></div>;
  }

  return (
    <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12" data-testid="dashboard-page">
      <div className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#009A44] mb-2">Dashboard</div>
        <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">
          Welcome, {user.name?.split(" ")[0]}
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">
          Signed in as <b>{user.email}</b> · <span className="text-[#009A44] uppercase text-xs font-bold">{user.role}</span>
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Stat label="Total orders" value={orders.length} />
        <Stat label="In 48hr hold" value={orders.filter(o => o.status === "payment_hold").length} color="#FFD100" />
        <Stat label="Confirmed" value={orders.filter(o => o.status === "confirmed").length} color="#009A44" />
        <Stat label="Shipped/Delivered" value={orders.filter(o => ["shipped", "delivered"].includes(o.status)).length} color="#121212" />
      </div>

      {orders.length === 0 ? (
        <div className="bg-white border border-[#E4E4E7] p-12 text-center">
          <Package size={48} className="mx-auto mb-4 text-zinc-400" />
          <h2 className="font-display text-2xl font-bold mb-2">No orders yet</h2>
          <p className="text-zinc-500 mb-5">Start browsing parts to place your first order.</p>
          <Link to="/search" className="btn-primary">Browse parts</Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map(o => <OrderCard key={o.order_id} order={o} highlighted={o.order_id === highlight} />)}
        </div>
      )}
    </main>
  );
}

function Stat({ label, value, color = "#52525B" }) {
  return (
    <div className="bg-white border border-[#E4E4E7] p-5">
      <div className="text-[11px] uppercase tracking-widest font-bold mb-1" style={{ color }}>{label}</div>
      <div className="font-display text-4xl font-extrabold">{value}</div>
    </div>
  );
}

function OrderCard({ order, highlighted }) {
  const meta = STATUS_META[order.status] || { color: "#52525B", label: order.status };
  const holdDate = order.payment_hold_expires_at ? new Date(order.payment_hold_expires_at) : null;
  const isHold = order.status === "payment_hold";

  const copyId = () => { navigator.clipboard.writeText(order.order_id); toast.success("Order ID copied"); };

  return (
    <div className={`bg-white border ${highlighted ? "border-[#009A44] ring-2 ring-[#009A44]/30" : "border-[#E4E4E7]"} p-5`} data-testid={`order-${order.order_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs">
            <button onClick={copyId} className="font-mono text-zinc-600 hover:text-[#009A44] flex items-center gap-1">
              {order.order_id} <Copy size={12} />
            </button>
            <div className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: meta.color }}>
              {meta.label}
            </div>
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Placed {new Date(order.created_at).toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Total</div>
          <div className="font-display text-2xl font-extrabold">{formatMoney(order.total)}</div>
        </div>
      </div>

      {isHold && holdDate && (
        <div className="bg-[#FFD100]/20 border-l-4 border-[#FFD100] p-3 mb-4 text-sm flex items-center gap-3">
          <Clock size={18} />
          <div>
            <b>Availability lock expires:</b> {holdDate.toLocaleString()}
            <div className="text-xs text-zinc-600">Full charge occurs only once the part is confirmed in our network.</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
        <InfoRow icon={<MapPin size={14} />} label="Deliver to" value={`${order.delivery_country} — ${order.delivery_address}`} />
        <InfoRow
          icon={order.shipping_method === "air" ? <Plane size={14} /> : <Ship size={14} />}
          label="Shipping"
          value={`${order.shipping_method === "air" ? "Air" : "Sea"} freight · ${order.shipping_eta_days} days`}
        />
        <InfoRow icon={<Package size={14} />} label="Items" value={`${order.items.length} part${order.items.length > 1 ? "s" : ""}`} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {order.items.map(it => (
          <div key={it.part_id} className="flex gap-2 items-center border border-[#E4E4E7] p-2">
            <img src={it.image_url} alt="" className="w-12 h-12 object-cover bg-zinc-100" />
            <div className="flex-1 min-w-0 text-xs">
              <Link to={`/parts/${it.part_id}`} className="font-semibold line-clamp-2 hover:text-[#009A44]">{it.name}</Link>
              <div className="text-zinc-500">× {it.quantity} · {formatMoney(it.line_total)}</div>
            </div>
          </div>
        ))}
      </div>

      {order.tracking_events?.length > 0 && (
        <div className="border-t border-[#E4E4E7] pt-4">
          <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 mb-3">Tracking</div>
          <ul className="space-y-2">
            {order.tracking_events.map((e, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle2 size={14} className="mt-0.5 text-[#009A44] shrink-0" />
                <div>
                  <div className="font-semibold">{e.label}</div>
                  <div className="text-xs text-zinc-500">{new Date(e.at).toLocaleString()} — {e.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-1">{icon} {label}</div>
      <div className="font-semibold line-clamp-2">{value}</div>
    </div>
  );
}
